import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { mapAssessmentScoreToDescription } from '@uplift/utils';
import { AssessmentReviewTable } from './AssessmentReviewTable';
import { useL10n } from '@uplift/hooks';
import { AssessmentKeys, FormSubmission } from '@uplift/types';
import { InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '../../../Tooltip';
import { useUserQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { useTimezone } from '@uplift/contexts';
import { PercentageDiffCircle } from '../../../UserActivityItem';
import { MedicalNoteSubmittedBySimple } from '../patient-chart-review-modal/components/ActivityDetailedView/MedicalNotes/MedicalNoteSubmittedBy.component';


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  headerRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  scoreSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }
}));


export type AssessmentViewProps = {
  submission: FormSubmission;
  assessmentKey: AssessmentKeys;
  score: number;
  percentage?: number;
};

const descriptionByAssessmentKey: Partial<Record<AssessmentKeys, string>> = {
  phq9: 'Clients automatically receive a PHQ9 before their first session and third session, and then every fifth session. Clients have one week after their session to complete the assessment before it expires.',
  gad7: 'Clients automatically receive a GAD7 before their first session and third session, and then every fifth session. Clients have one week after their session to complete the assessment before it expires.',
};


export const AssessmentView: React.FC<AssessmentViewProps> = (props) => {
  const l10n = useL10n();
  const mapScoreToSummary = mapAssessmentScoreToDescription(props.assessmentKey, l10n);
  const { classes } = useStyles();
  const timezone = useTimezone();

  const completedAt = React.useMemo(() => {
    if (!props.submission?.completedAt) return '';

    const currentDate = CalendarDateService.now(timezone);
    const parsedTimestamp = CalendarDateService.date(props.submission.completedAt).tz(timezone);

    if (parsedTimestamp.year() !== currentDate.year()) {
      return parsedTimestamp.format('MM/DD/YYYY');
    }
    return parsedTimestamp.format('MM/DD');
  }, [ props.submission.completedAt ]);

  return (
    <>
      <Box className={classes.headerRoot}>
        <Box className={classes.scoreSection}>
          {typeof props.percentage === 'number' && (
            <PercentageDiffCircle percentage={props.percentage} />
          )}
          <Typography><strong>{props.score}</strong> {mapScoreToSummary(props.score)}.</Typography>
          <Typography color="textSecondary">{l10n.clinicalNotes.completedAt(completedAt)}</Typography>
        </Box>
        {descriptionByAssessmentKey[props.assessmentKey] && (
          <Tooltip
            light
            placement="left"
            title={(
              <Typography color="textSecondary">
                {descriptionByAssessmentKey[props.assessmentKey]}
              </Typography>
            )}
          >
            <IconButton>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <AssessmentReviewTable formSubmissionId={props.submission.formSubmissionId} />
      <MedicalNoteSubmittedBySimple userId={props.submission.userId} submittedAt={props.submission.completedAt} />
    </>
  );
};


export const AssessmentExpiredView: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data } = useUserQuery(patientId);

  return (
    <Box>
      {data?.firstName} did not complete the assessment before it expired.
    </Box>
  );
};


export const AssessmentInitialView: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data } = useUserQuery(patientId);

  return (
    <Box>
      {data?.firstName} has not completed this assessment.
    </Box>
  );
};


export const FormInitialView: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data } = useUserQuery(patientId);

  return (
    <Box>
      {data?.firstName} has not completed this form.
    </Box>
  );
};
